import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, Typography, AccordionSummary, Divider, List, ListItem, ListItemText, Paper, TableHead, useMediaQuery, useTheme } from "@mui/material";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdsComponent from "../../components/AdComponent";
import CustomButton from '../../components/CustomButton';
import CustomCard from '../../components/CustomCard';
import CustomTextField from '../../components/CustomTextField';
import CustomTypography from "../../components/CustomTypography";
import { triggerGTMEvent } from "../../components/GTMEvent";
import { CurrencyFormatterUtils } from '../currency-formatter/currency-formatter-utils';
import * as Data from './emi-calculator-data';
import { setLoanAmount, setRateOfInterest, setTenureInYears } from './emi-calculator-reducer'; // Path to your reducer
import * as Utils from './emi-calculator-utils';

const Example = ({ router }) => {
    const dispatch = useDispatch();
    const loanAmount = useSelector((state) => state.emiCalculator.loanAmount);
    const tenureInYears = useSelector((state) => state.emiCalculator.tenureInYears);
    const rateOfInterest = useSelector((state) => state.emiCalculator.rateOfInterest);
    const currencyFormatter = useSelector((state) => state.currencyDataFormatter.formatter || {});
    const [report, setReport] = useState(Utils.generateReport(loanAmount, tenureInYears, rateOfInterest));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const scrollToTopRef = useRef(null);
    useEffect(() => {
        scrollToTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);


    const navigateTo = (path) => {
        router.navigate(path); // This navigates to the specific page
    };

    const handleLoanAmount = (event) => {
        dispatch(setLoanAmount(event.target.value));
        setReport(null);
    }

    const handleTenureInYears = (event) => {
        dispatch(setTenureInYears(event.target.value));
        setReport(null);
    }

    const handleRateOfInterest = (event) => {
        dispatch(setRateOfInterest(event.target.value));
        setReport(null);
    }

    const generateReport = () => {
        triggerGTMEvent("Generate Report", "EMI > Generate Report");
        const report = Utils.generateReport(loanAmount, tenureInYears, rateOfInterest);
        setReport(report);
    };

    const createLoanRecordsAccordion = (report) => {
        // Grouping data by year
        const groupedData = report.amortizationSchedule.reduce((acc, item) => {
            if (!acc[item.year]) acc[item.year] = [];
            acc[item.year].push(item);
            return acc;
        }, {});

        return (
            <>
                {Object.keys(groupedData).map((year) => (
                    <Accordion key={year} sx={{ marginBottom: { xs: 1, sm: 2 } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${year}-content`}
                            id={`panel-${year}-header`}
                            sx={{
                                backgroundColor: isMobile ? '#f5f5f5' : '#e0e0e0',
                                padding: { xs: '8px 12px', sm: '12px 16px' },
                            }}
                        >
                            <CustomTypography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                    fontWeight: 500,
                                }}
                            >
                                {year}
                            </CustomTypography>
                        </AccordionSummary>

                        <AccordionDetails sx={{ padding: { xs: 1, sm: 2 } }}>
                            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                                <Table size={isMobile ? 'small' : 'medium'}>
                                    <TableHead>
                                        <TableRow>
                                            {['Month', 'EMI', 'Principal', 'Interest', 'Remaining Balance'].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                                        padding: { xs: '4px 8px', sm: '6px 12px' },
                                                    }}
                                                >
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {groupedData[year].map((item) => (
                                            <TableRow key={item.month}>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][item.month - 1]}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {CurrencyFormatterUtils(item.emi, currencyFormatter)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {CurrencyFormatterUtils(item.principal, currencyFormatter)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {CurrencyFormatterUtils(item.interest, currencyFormatter)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {CurrencyFormatterUtils(item.remainingBalance, currencyFormatter)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </>
        );
    };

    return (
        <div ref={scrollToTopRef}>

            {/* BREADCRUMPS WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }} sx={{ margin: '10px' }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" onClick={() => navigateTo('/')}>
                            Home
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>EMI Calculator</Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
            {/* BREADCRUMPS WIDGET ENDS */}

            {/* INPUT WIDGET START */}
            <Grid container spacing={1}>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    <CustomCard
                        title="Loan Details"
                    >
                        <CustomTextField
                            label="Loan Amount"
                            value={loanAmount}
                            type="number"
                            onChange={handleLoanAmount}
                        />
                        <CustomTextField
                            label="Tenure (In Years)"
                            value={tenureInYears}
                            type="number"
                            onChange={handleTenureInYears}
                        />
                        <CustomTextField
                            label="Rate of Interest"
                            value={rateOfInterest}
                            type="number"
                            onChange={handleRateOfInterest}
                        />
                        <CustomButton
                            variant="contained"
                            color="success"
                            onClick={generateReport}
                            fullWidth={true}
                        >
                            Calculate
                        </CustomButton>
                    </CustomCard>
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    {report &&
                        <div>
                            <CustomCard
                                title="EMI"
                                description={
                                    <>For Loan amount of <strong>{CurrencyFormatterUtils(Data.getLoanAmount(report), currencyFormatter)}</strong>, you need to pay a monthly EMI of</>
                                }
                            >
                                <CustomTypography variant="h6" color="success" fontWeight="bold">
                                    {CurrencyFormatterUtils(Data.getEMI(report), currencyFormatter)}
                                </CustomTypography>
                            </CustomCard>
                            <CustomCard
                                title="Interest Breakdown"
                                description="You need to pay a total interest to the bank over the loan tenure"
                            >
                                <CustomTypography variant="h6" color="success" fontWeight="bold">
                                    {CurrencyFormatterUtils(Data.getInterestPayable(report), currencyFormatter)}
                                    ({Data.getInterestPercentage(report)})
                                </CustomTypography>
                            </CustomCard>
                        </div>
                    }
                </Grid>
            </Grid>
            {/* INPUT WIDGET END */}

            {/* REPORT WIDGET START */}
            {
                report &&
                <Grid container spacing={1} >
                    <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomCard
                            title="Amortization Schedule"
                            description="Detailed breakdown of loan payments, principal, interest, and balance."
                        >
                            {createLoanRecordsAccordion(report)}
                        </CustomCard>
                    </Grid>
                </Grid >
            }
            {/* REPORT WIDGET END */}

            {/* AD WIDGET START */}
            {/* <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <AdsComponent dataAdSlot='6489195805' />
                </Grid>
            </Grid> */}
            {/* AD WIDGET START */}


            {/* BLOG WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomCard>
                        <CustomTypography variant="h5" gutterBottom>
                            Why Use Our EMI Calculator? Understanding the Benefits
                        </CustomTypography>
                        <Divider sx={{ mb: 2 }} />

                        <CustomTypography variant="body1" paragraph>
                            Managing finances effectively is essential for anyone taking a loan. Our EMI Calculator helps you plan your
                            repayments with precision, ensuring that you make informed decisions before committing to a loan. Here’s why
                            this tool is beneficial:
                        </CustomTypography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="1. Accurate EMI Estimation"
                                    secondary="With just a few inputs—loan amount, interest rate, and tenure—you can instantly calculate your monthly EMI. This allows you to plan your monthly budget effectively."
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemText
                                    primary="2. Interest Payable Breakdown"
                                    secondary="The calculator not only shows your EMI but also provides a detailed breakdown of the interest you’ll pay over the tenure. This helps you understand the total cost of your loan."
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemText
                                    primary="3. Total Loan Repayment Insights"
                                    secondary="You can see the total amount payable to the bank, including both the principal and interest. This ensures transparency in your loan calculations."
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemText
                                    primary="4. Amortization Schedule"
                                    secondary="A detailed report of your loan repayment schedule is available, showing how each EMI is split between principal repayment and interest payment. This helps you track how your loan balance reduces over time."
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemText
                                    primary="5. Better Financial Planning"
                                    secondary="Knowing your EMI in advance allows you to plan your expenses wisely. You can determine whether a loan fits within your budget or if you need to adjust your loan terms."
                                />
                            </ListItem>

                        </List>

                        <CustomTypography variant="h5" gutterBottom>
                            How to Use the EMI Calculator Effectively?
                        </CustomTypography>
                        <Divider sx={{ mb: 2 }} />

                        <List>
                            <ListItem>
                                <ListItemText primary="Enter Loan Details" secondary="Provide the loan amount, tenure, and interest rate." />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Analyze the EMI Report" secondary="Understand the monthly payment amount and total interest payable." />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Experiment with Different Values" secondary="Adjust the loan parameters to find the most affordable option." />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Use the Amortization Schedule" secondary="Track how your loan is repaid over time." />
                            </ListItem>
                        </List>

                        <CustomTypography variant="h5" gutterBottom>
                            Final Thoughts
                        </CustomTypography>
                        <Divider sx={{ mb: 2 }} />
                        <CustomTypography variant="body1" paragraph>
                            Our EMI Calculator is designed to simplify loan planning and empower users with financial clarity. Whether
                            you’re taking a home loan, car loan, or personal loan, this tool ensures you stay financially prepared for
                            your repayment journey.
                        </CustomTypography>
                        <CustomTypography variant="body1" paragraph>
                            Try it now and make informed financial decisions!
                        </CustomTypography>
                    </CustomCard>
                </Grid>
            </Grid>
            {/* BLOG WIDGET START */}

        </div >
    );
};

export default Example;
